import type { GetCustomAnalyticsCatalogPreviewOutput } from "@redotech/admin-sdk/rpc/schema/custom-reports/get-custom-analytics-catalog-preview";
import type { GetCustomReportDebugOutput } from "@redotech/admin-sdk/rpc/schema/custom-reports/get-custom-report-debug";
import type { GetCustomReportsUsageOutput } from "@redotech/admin-sdk/rpc/schema/custom-reports/get-custom-reports-usage";
import type { ListCustomReportsOutput } from "@redotech/admin-sdk/rpc/schema/custom-reports/list-custom-reports";
import type { PublishCustomReportAstOutput } from "@redotech/admin-sdk/rpc/schema/custom-reports/publish-custom-report-ast";
import type { ValidateCustomReportAstOutput } from "@redotech/admin-sdk/rpc/schema/custom-reports/validate-custom-report-ast";

export enum CustomReportsTab {
  Overview = "overview",
  Reports = "reports",
  Debug = "debug",
  Author = "author",
  Catalog = "catalog",
  Relationships = "relationships",
}

export type CustomReportListRow = ListCustomReportsOutput["reports"][number];
export type DailyUsageRow = GetCustomReportsUsageOutput["daily"][number];
export type FailureCodeRow =
  GetCustomReportsUsageOutput["failureCodes"][number];
export type RecentFailureRow =
  GetCustomReportsUsageOutput["recentFailures"][number];
export type CatalogPreviewSection =
  GetCustomAnalyticsCatalogPreviewOutput["sections"][number];
export type CatalogPreviewItem = CatalogPreviewSection["items"][number];

export interface CatalogGrainDictionary {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  attributes: CatalogPreviewItem[];
  measures: CatalogPreviewItem[];
  metrics: CatalogPreviewItem[];
  defaultFilters: CatalogPreviewItem[];
  semanticTables: CatalogPreviewItem[];
  relationships: CatalogPreviewItem[];
  columnFilters: CatalogPreviewItem[];
  crossGrainMetrics: CatalogPreviewItem[];
  selectableCatalogs: CatalogPreviewItem[];
}

export interface CatalogSearchResultGroup {
  id: string;
  title: string;
  description: string | null;
  items: CatalogPreviewItem[];
}

export interface CatalogDetailField {
  label: string;
  value: string;
}

export interface CatalogTokenMatch {
  title: string;
  kind: string;
  reference: string;
  description: string | null;
  search: string;
}

export interface CatalogRelationshipGraphGrain {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  attributeCount: number;
  measureCount: number;
  metricCount: number;
}

export interface CatalogRelationshipGraphRelationship {
  id: string;
  pairId: string;
  title: string;
  description: string | null;
  base: string;
  target: string;
  direction: string;
  cardinality: string | null;
  optional: boolean;
}

export interface CatalogRelationshipField {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  kind: string;
  owningGrain: string | null;
  inputMetricRefs: readonly string[];
  searchText: string;
}

export interface CatalogRelationshipPathStep {
  relationshipId: string;
  relationshipName: string;
  from: string;
  to: string;
  direction: string;
  joinType: string | null;
  cardinality: string | null;
  optional: boolean;
  description: string | null;
}

export interface CatalogRelationshipFieldAnalysis {
  baseGrain: CatalogRelationshipGraphGrain;
  crossGrainMetricInputs: CatalogRelationshipMetricInput[];
  field: CatalogRelationshipField;
  selectableItem: CatalogPreviewItem | null;
  projectionKind: string | null;
  optionalMany: boolean;
  coalesceMissingMetricValuesToZero: boolean;
  selectedPath: CatalogRelationshipPathStep[];
  availablePaths: CatalogRelationshipPathStep[][];
  status: "base" | "selectable" | "unavailable";
}

export interface CatalogRelationshipMetricInput {
  refKey: string;
  title: string;
  owningGrain: string | null;
  selectedPath: CatalogRelationshipPathStep[];
  availablePaths: CatalogRelationshipPathStep[][];
  status: "reachable" | "missing" | "unavailable";
}

export interface CatalogRelationshipGraphModel {
  grains: CatalogRelationshipGraphGrain[];
  relationships: CatalogRelationshipGraphRelationship[];
  fields: CatalogRelationshipField[];
  selectableCatalogItems: CatalogPreviewItem[];
}

export type CodeTokenTone =
  | "plain"
  | "keyword"
  | "string"
  | "number"
  | "boolean"
  | "operator"
  | "property"
  | "catalog";

export interface CodeToken {
  text: string;
  tone: CodeTokenTone;
  catalogMatch?: CatalogTokenMatch;
}

export type SnowflakeSqlDebugOutput = NonNullable<
  GetCustomReportDebugOutput["snowflake"]
>;
export type SqlMode = "inline" | "bound";

export type DebugState =
  | { type: "idle" }
  | { type: "success"; result: GetCustomReportDebugOutput }
  | { type: "error"; message: string };

export type AuthorValidateState =
  | { type: "idle" }
  | { type: "success"; result: ValidateCustomReportAstOutput }
  | { type: "error"; message: string };

export type AuthorPublishState =
  | { type: "idle" }
  | { type: "success"; result: PublishCustomReportAstOutput }
  | { type: "error"; message: string };

export type AuthorPreviewColumn = NonNullable<
  ValidateCustomReportAstOutput["columns"]
>[number];

export type AuthorPreviewRow = NonNullable<
  ValidateCustomReportAstOutput["rows"]
>[number];
